import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';

const imgClassName = 'w-full h-full object-cover';

const testimonialGenericRachel = {
  quote: (
    <>
      <span className="text-3xl">This app</span> just saved me so much time.
      Thank you!
    </>
  ),
  name: 'Rachel Silva',
  title: 'Teacher, US 🇺🇸',
  img: (
    <StaticImage
      src="../../images/landing/testimonials/groups-of-4-04-24.jpg"
      alt="A room layout with groups of 4-person tables, formed by placing single-desks next to each other."
      className={imgClassName}
      width={705}
    />
  ),
};

type Testimonial = typeof testimonialGenericRachel;

const testimonialClassroomMareike: Testimonial = {
  quote: (
    <>
      <span className="text-3xl">I teach</span> the same set of students in
      various classrooms throughout the week. The beauty of the Seating Chart
      Maker for me lies in its ability to utilize the same student list to form
      multiple seating arrangements for different rooms.
    </>
  ),
  name: 'Mareike Wolff',
  title: 'Teacher, Switzerland 🇨🇭',
  img: (
    <StaticImage
      src="../../images/landing/testimonials/grid-04-24.jpg"
      alt="A simple classroom layout with a grid of desks."
      className={imgClassName}
      width={705}
    />
  ),
};

const testimonialVenueTeresa: Testimonial = {
  quote: (
    <>
      <span className="text-3xl">Seating chart maker</span> made planning our
      table layout simple. Highly recommended for venue planning!
    </>
  ),
  name: 'Teresa M.',
  title: 'Illinois US 🇺🇸',
  img: (
    <StaticImage
      src="../../images/landing/testimonials/round-6.jpg"
      alt="A room layout with 6-person round tables."
      className={imgClassName}
      width={705}
    />
  ),
};

const testimonialEventLisa: Testimonial = {
  quote: (
    <>
      <span className="text-3xl">Being able</span> to create multiple versions
      of our seating chart with such ease allowed us to experiment with
      different setups until we found the perfect one for the event.
    </>
  ),
  name: 'Lisa L.',
  title: 'Pennsylvania US 🇺🇸',
  img: (
    <StaticImage
      src="../../images/landing/testimonials/long-3.jpg"
      alt="A room layout with 3 long tables."
      className={imgClassName}
      width={705}
    />
  ),
};

const Testimonials: React.FC<{ content: [Testimonial, Testimonial] }> = ({
  content,
}) => (
  <section className="bg-emerald-800">
    <div className="max-w-7xl mx-auto md:grid md:grid-cols-2">
      <div className="relative py-24 px-8 bg-emerald-800 overflow-hidden lg:px-16 lg:grid lg:grid-cols-4 lg:gap-x-8 md:border-r md:border-emerald-900">
        <div className="absolute inset-0 opacity-50 filter saturate-0 mix-blend-multiply">
          {content[0].img}
        </div>
        <div className="relative md:flex md:flex-grow lg:col-span-3">
          <blockquote className="mt-6 md:flex-grow md:flex md:flex-col">
            <div className="relative text-lg font-medium text-white md:flex-grow">
              <svg
                className="absolute top-0 left-0 transform -translate-x-4 -translate-y-3 h-8 w-8 text-emerald-600"
                fill="currentColor"
                viewBox="0 0 32 32"
              >
                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
              </svg>
              <p className="relative">{content[0].quote}</p>
            </div>
            <footer className="mt-8">
              <div className="text-base font-light text-white">
                {content[0].name}
              </div>
              <div className="text-base font-medium text-emerald-200">
                {content[0].title}
              </div>
            </footer>
          </blockquote>
        </div>
      </div>
      <div className="relative py-24 px-8 bg-emerald-800 overflow-hidden lg:px-16 lg:grid lg:grid-cols-4 lg:gap-x-8 border-t-2 border-gray-900 md:border-t-0 md:border-l-2">
        <div className="absolute inset-0 opacity-50 filter saturate-0 mix-blend-multiply">
          {content[1].img}
        </div>
        <div className="relative lg:col-span-3">
          <blockquote className="mt-6 md:flex-grow md:flex md:flex-col">
            <div className="relative text-lg font-medium text-white md:flex-grow">
              <svg
                className="absolute top-0 left-0 transform -translate-x-4 -translate-y-3 h-8 w-8 text-emerald-600"
                fill="currentColor"
                viewBox="0 0 32 32"
              >
                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
              </svg>
              <p className="relative">{content[1].quote}</p>
            </div>
            <footer className="mt-8">
              <div className="text-base font-light text-white">
                {content[1].name}
              </div>
              <div className="text-base font-medium text-emerald-200">
                {content[1].title}
              </div>
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  </section>
);

export const TestimonialsDefault = () => (
  <Testimonials content={[testimonialGenericRachel, testimonialVenueTeresa]} />
);

export const TestimonialsWedding = () => (
  <Testimonials content={[testimonialVenueTeresa, testimonialEventLisa]} />
);

export const TestimonialsClassroom = () => (
  <Testimonials
    content={[testimonialGenericRachel, testimonialClassroomMareike]}
  />
);
